import { LockIcon, LockOpenIcon } from "@phosphor-icons/react";
import clsx from "clsx";
import dayjs from "dayjs";
import { motion } from "motion/react";
import { useEffect, useEffectEvent, useRef, useState } from "react";

import { allowPurchaseExpiresAtStorage } from "@/utils/localStorage";

const ALLOW_MINUTES = 5;

const getRestoredSecondsLeft = () => {
  const stored = allowPurchaseExpiresAtStorage.get();
  if (!stored) return 0;

  const expiresAt = Number(stored);
  if (!expiresAt) return 0;

  const secondsLeft = dayjs(expiresAt).diff(dayjs(), "second");

  return Math.max(0, secondsLeft);
};

export const PurchaseToggleButton = () => {
  const bodyRef = useRef(window.document.body);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(() =>
    getRestoredSecondsLeft(),
  );

  const purchasesAllowed = secondsLeft > 0;

  const clearAllowPurchaseInterval = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  const disablePurchases = useEffectEvent(() => {
    allowPurchaseExpiresAtStorage.remove();
    clearAllowPurchaseInterval();
  });

  const setupAllowPurchaseInterval = () => {
    clearAllowPurchaseInterval();

    intervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
  };

  const startTimer = (remainingSeconds: number) => {
    setSecondsLeft(remainingSeconds);
    setupAllowPurchaseInterval();
  };

  const enablePurchases = () => {
    const totalSeconds = ALLOW_MINUTES * 60;
    const expiresAt = dayjs().add(ALLOW_MINUTES, "minute").valueOf();

    allowPurchaseExpiresAtStorage.set(String(expiresAt));
    startTimer(totalSeconds);
  };

  const handleClick = () => {
    if (purchasesAllowed) {
      setSecondsLeft(0);
    } else {
      enablePurchases();
    }
  };

  const restoreInterval = useEffectEvent(() => {
    if (getRestoredSecondsLeft() > 0) {
      setupAllowPurchaseInterval();
    } else {
      allowPurchaseExpiresAtStorage.remove();
      clearAllowPurchaseInterval();
    }

    return () => clearAllowPurchaseInterval();
  });

  useEffect(() => {
    restoreInterval();
  }, []);

  useEffect(() => {
    if (secondsLeft <= 0) {
      disablePurchases();
      clearInterval(intervalRef.current!);
    }
  }, [secondsLeft]);

  return (
    <motion.button
      drag
      dragMomentum={false}
      dragConstraints={bodyRef}
      onClick={handleClick}
      className={clsx(
        "fixed z-9999 flex cursor-pointer items-center gap-1.5 select-none",
        "rounded-full border-0 px-3 py-2",
        "text-xs leading-tight font-semibold text-white",
        "shadow-md transition-colors duration-200",
        "touch-none",
        purchasesAllowed
          ? "bg-semantic-error-strong"
          : "bg-surface-neutral-strong",
      )}
    >
      {purchasesAllowed ? (
        <LockOpenIcon size={16} weight="bold" />
      ) : (
        <LockIcon size={16} weight="bold" />
      )}
      <span className="flex flex-col gap-px">
        <span>
          {purchasesAllowed ? "Purchases allowed" : "Purchases blocked"}
        </span>
        {purchasesAllowed ? (
          <span className="text-[10px]">
            Disables in {dayjs.duration(secondsLeft, "seconds").format("mm:ss")}
          </span>
        ) : !purchasesAllowed ? (
          <span className="text-[10px]">Tap to unlock</span>
        ) : null}
      </span>
    </motion.button>
  );
};
